/**
 * FornadaViewDialog Component
 */

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Pencil } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import type { FornadaCompleta } from "@/types/fornada";

interface FornadaViewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fornada: FornadaCompleta | null;
  onEdit: () => void;
}

export function FornadaViewDialog({ open, onOpenChange, fornada, onEdit }: FornadaViewDialogProps) {
  if (!fornada) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="font-mono text-primary">
              #FOR{fornada.fornada.numero_fornada?.toString().padStart(6, '0') || 'N/A'}
            </span>
            <span>-</span>
            <span>Detalhes da Fornada</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Cabeçalho */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Data da Fornada</p>
              <p className="font-medium">
                {format(new Date(fornada.fornada.data_fornada), "dd/MM/yyyy", { locale: ptBR })}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Responsável</p>
              <p className="font-medium">{fornada.fornada.responsavel || "-"}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Destino</p>
              <p className="font-medium capitalize">{fornada.fornada.destino || "-"}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Débito de Estoque</p>
              <p className="font-medium">
                {fornada.fornada.debitar_estoque ? "Sim" : "Não"}
                {fornada.fornada.estoque_debitado && " (✓ Debitado)"}
              </p>
            </div>
          </div>

          {fornada.fornada.observacoes_gerais && (
            <div>
              <p className="text-sm text-muted-foreground">Observações Gerais</p>
              <p className="font-medium whitespace-pre-wrap">{fornada.fornada.observacoes_gerais}</p>
            </div>
          )}

          <Separator />

          {/* Rendimento e Tempo */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Rendimento Total</p>
              <p className="font-medium">
                {fornada.fornada.rendimento_total ? `${fornada.fornada.rendimento_total} unidades` : "-"}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Tempo Total</p>
              <p className="font-medium">
                {fornada.fornada.tempo_total_minutos ? `${fornada.fornada.tempo_total_minutos} minutos` : "-"}
              </p>
            </div>
          </div>

          <Separator />

          {/* Itens */}
          <div>
            <h3 className="font-semibold mb-4">Receitas da Fornada</h3>
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Receita</TableHead>
                    <TableHead className="text-right">Quantidade</TableHead>
                    <TableHead className="text-right">Custo Unit.</TableHead>
                    <TableHead className="text-right">Custo Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {fornada.itens.map((item) => (
                    <TableRow key={item.id_fornada_item}>
                      <TableCell>{item.receita_nome}</TableCell>
                      <TableCell className="text-right">{item.quantidade_produzida}</TableCell>
                      <TableCell className="text-right">R$ {item.custo_unitario_fixado.toFixed(2)}</TableCell>
                      <TableCell className="text-right font-semibold">R$ {item.custo_total_item.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          <Separator />

          {/* Custos Adicionais */}
          {fornada.custos_adicionais && fornada.custos_adicionais.length > 0 && (
            <>
              <div>
                <h3 className="font-semibold mb-4">Itens Avulsos</h3>
                <div className="rounded-md border overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Descrição</TableHead>
                        <TableHead className="text-right">Quantidade</TableHead>
                        <TableHead className="text-right">Valor Unit.</TableHead>
                        <TableHead className="text-right">Valor Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {fornada.custos_adicionais.map((custo) => (
                        <TableRow key={custo.id_fornada_custo_adicional}>
                          <TableCell>{custo.descricao}</TableCell>
                          <TableCell className="text-right">{custo.quantidade}</TableCell>
                          <TableCell className="text-right">R$ {custo.valor_unitario.toFixed(2)}</TableCell>
                          <TableCell className="text-right font-semibold">
                            R$ {custo.valor_total.toFixed(2)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
              <Separator />
            </>
          )}

          {/* Custos Operacionais */}
          {(fornada.fornada.custo_mao_obra || fornada.fornada.custo_gas || fornada.fornada.outras_despesas) && (
            <>
              <div>
                <h3 className="font-semibold mb-4">Custos Operacionais</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {fornada.fornada.custo_mao_obra && (
                    <div>
                      <p className="text-sm text-muted-foreground">Mão de Obra</p>
                      <p className="font-medium">R$ {fornada.fornada.custo_mao_obra.toFixed(2)}</p>
                      {fornada.fornada.valor_hora_profissional && (
                        <p className="text-xs text-muted-foreground">
                          R$ {fornada.fornada.valor_hora_profissional.toFixed(2)}/hora
                        </p>
                      )}
                    </div>
                  )}
                  {fornada.fornada.custo_gas && (
                    <div>
                      <p className="text-sm text-muted-foreground">
                        Gás {fornada.fornada.tipo_gas && `(${fornada.fornada.tipo_gas})`}
                      </p>
                      <p className="font-medium">R$ {fornada.fornada.custo_gas.toFixed(2)}</p>
                    </div>
                  )}
                  {fornada.fornada.outras_despesas && (
                    <div>
                      <p className="text-sm text-muted-foreground">Outras Despesas</p>
                      <p className="font-medium">R$ {fornada.fornada.outras_despesas.toFixed(2)}</p>
                    </div>
                  )}
                </div>
              </div>
              <Separator />
            </>
          )}

          {/* Totalizadores */}
          <div className="bg-muted p-4 rounded-md space-y-2">
            <div className="flex justify-between text-sm">
              <span>Custo Receitas:</span>
              <span>
                R$ {fornada.itens.reduce((sum, item) => sum + item.custo_total_item, 0).toFixed(2)}
              </span>
            </div>
            {fornada.fornada.custo_mao_obra && (
              <div className="flex justify-between text-sm">
                <span>Custo Mão de Obra:</span>
                <span>R$ {fornada.fornada.custo_mao_obra.toFixed(2)}</span>
              </div>
            )}
            {fornada.fornada.custo_gas && (
              <div className="flex justify-between text-sm">
                <span>Custo Gás:</span>
                <span>R$ {fornada.fornada.custo_gas.toFixed(2)}</span>
              </div>
            )}
            {fornada.custos_adicionais && fornada.custos_adicionais.length > 0 && (
              <div className="flex justify-between text-sm">
                <span>Itens Avulsos:</span>
                <span>
                  R$ {fornada.custos_adicionais.reduce((sum, c) => sum + c.valor_total, 0).toFixed(2)}
                </span>
              </div>
            )}
            {fornada.fornada.outras_despesas && (
              <div className="flex justify-between text-sm">
                <span>Outras Despesas:</span>
                <span>R$ {fornada.fornada.outras_despesas.toFixed(2)}</span>
              </div>
            )}
            <Separator />
            <div className="flex justify-between items-center">
              <span className="font-medium">Custo Total:</span>
              <span className="font-bold text-lg">R$ {fornada.fornada.custo_total.toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center text-sm text-muted-foreground">
              <span>Custo Médio por Unidade:</span>
              <span className="font-semibold">R$ {fornada.fornada.custo_medio_unitario.toFixed(2)}</span>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={onEdit} className="gap-2">
            <Pencil className="w-4 h-4" />
            Editar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
